import * as React from "react";
import { FloatingFocusManager, FloatingPortal } from "@floating-ui/react";

/**
 * How a floating surface gets onto the page — and the seam that lets a container change it.
 *
 * ── WHY THIS IS AN OVERRIDE POINT AT ALL ──────────────────────────────────────────────────
 *
 * By default a floating surface portals to the end of the document, which is what keeps it above
 * everything and out of any ancestor's `overflow: hidden`. Inside a DIALOG that is wrong: the
 * dialog is itself a portalled, stacking-context-forming layer, so a menu that portals to the
 * document body escapes it and can end up painted BEHIND the dialog that opened it.
 *
 * `dialog.tsx` therefore provides its own component that renders in place. This is the contract
 * that makes that possible.
 *
 * ── WHAT THIS PORT DOES NOT DO ────────────────────────────────────────────────────────────
 *
 * Our own floating components — Menu, Overlay, Drawer, Dialog — do NOT consult this yet; each
 * reaches for `FloatingPortal` directly. That is unchanged by this port: they did not consult
 * the vendor's version either, so nothing behaves differently today. But it does mean the
 * provider `dialog.tsx` mounts is currently inert, and a menu opened inside a dialog still
 * portals out of it.
 *
 * Wiring them up is a real change to how every menu renders and is deliberately NOT bundled
 * here: it could not be verified in the browser during this port, and an unverified change to
 * the rendering path of every floating surface is not something to ship on reasoning alone.
 */
export interface FloatingComponentProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Passed straight to `FloatingFocusManager` when the surface needs to trap or move focus. */
  focusManagerProps?: React.ComponentProps<typeof FloatingFocusManager>;
  height?: number;
  left?: number;
  /** Whether the page behind should stop scrolling while this is open. */
  lockScroll?: boolean;
  open: boolean;
  position?: "absolute" | "fixed";
  top?: number;
  width?: number;
}

export const DefaultFloatingComponent = React.forwardRef<HTMLDivElement, FloatingComponentProps>(
  function DefaultFloatingComponent(
    {
      focusManagerProps,
      height: _height,
      left,
      lockScroll: _lockScroll,
      open,
      position,
      style: styleProp,
      top,
      width: _width,
      ...rest
    },
    ref,
  ) {
    if (!open) return null;
    // `width`/`height` are measurements the caller passes back for its own layout maths; they
    // are deliberately NOT written as styles, or the surface would be frozen at whatever size it
    // happened to have when it was last measured.
    const style = { ...styleProp, left, position, top };
    const content = <div style={style} {...rest} ref={ref} />;
    return (
      <FloatingPortal>
        {focusManagerProps ? (
          <FloatingFocusManager {...focusManagerProps}>{content}</FloatingFocusManager>
        ) : (
          content
        )}
      </FloatingPortal>
    );
  },
);

const FloatingComponentContext = React.createContext<{
  Component: React.ComponentType<FloatingComponentProps & React.RefAttributes<HTMLDivElement>>;
}>({ Component: DefaultFloatingComponent });

export function FloatingComponentProvider({
  Component,
  children,
}: {
  Component: React.ComponentType<FloatingComponentProps & React.RefAttributes<HTMLDivElement>>;
  children?: React.ReactNode;
}) {
  const value = React.useMemo(() => ({ Component }), [Component]);
  return (
    <FloatingComponentContext.Provider value={value}>{children}</FloatingComponentContext.Provider>
  );
}

export function useFloatingComponent() {
  return React.useContext(FloatingComponentContext);
}
